import React from 'react'
import { useIntl } from 'react-intl'
import ReactDOMServer from 'react-dom/server'
import { css } from '@emotion/css'
import { SessionMetadataActionBar } from '../session-metadata'
import { Chart } from '../../legacy-design-system/charts'
import { InformationalPill } from '@contrast-security-inc/design-system-badges'
import {
  IconInformation,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { dateFormats } from '../react-intl-helpers'

const formatEmptyState = (val) =>
  isNaN(val) || typeof val === 'undefined' ? '-' : val
const flatPanelCss = css`
  background-color: ${theme.color.white};
  padding: ${theme.spacing.px24};
  border-radius: ${theme.spacing.px8};
  border: 1px solid ${theme.color.neutral40};
`
export const RouteCoverageSummary = ({ sessionMetadataActionProps, stats }) => {
  const intl = useIntl()
  const {
    // session metadata
    total: smdTotal,
    exercisedCount: smdCount,
    // exercised counts
    exercisedCountExceptSessionMetadataFilter: exercisedCount,
    totalCountExceptSessionMetadataFilter: exercisedTotal
  } = stats

  return (
    <Stack flexDirection="column" testId="route-coverage-summary">
      <Stack alignItems="center">
        <Text font="titleH3">
          {intl.formatMessage({ id: 'title_routeCoverageSummary' })}
        </Text>
        <Tooltip
          interactive={true}
          body={intl.formatMessage(
            { id: 'title_routeCoverageSummaryTooltip' },
            {
              a: (msg) => (
                <Link
                  isExternal={true}
                  href="https://docs.contrastsecurity.com/en/view-route-details.html"
                >
                  {msg}
                </Link>
              )
            }
          )}
        >
          <IconInformation />
        </Tooltip>
      </Stack>
      <Stack spacing="24px">
        <div className={flatPanelCss}>
          <ExercisedRoutesSummary
            exercisedStats={{ exercisedCount, exercisedTotal }}
          />
        </div>
        <div className={flatPanelCss} style={{ flex: 1 }}>
          <SessionMetadataSummary
            smdStats={{ smdCount, smdTotal }}
            sessionMetadataActionProps={sessionMetadataActionProps}
          />
        </div>
      </Stack>
    </Stack>
  )
}

const StatCount = ({ color, count, testId }) => {
  const colorValue =
    typeof count !== 'undefined' && !isNaN(count)
      ? color
      : theme.color.neutral50

  return (
    <Text font="metricLg" style={{ color: colorValue }} testId={testId}>
      {formatEmptyState(count)}
    </Text>
  )
}
const styleColorTurquise70 = { style: { color: theme.color.turquoise70 } }
const ExercisedRoutesSummary = ({ exercisedStats }) => {
  const intl = useIntl()
  const { exercisedCount, exercisedTotal } = exercisedStats
  const percentValue = Math.round((exercisedCount / exercisedTotal) * 100)

  return (
    <Stack
      flexDirection="column"
      spacing="24px"
      testId="exercised-routes-summary"
    >
      <Text font="titleH5" {...styleColorTurquise70}>
        {intl.formatMessage({ id: 'title_exercisedRoutes' })}
      </Text>
      <Stack alignItems="center" spacing="24px">
        <RoutesExercisedChart percentValue={percentValue} />
        <Stack flexDirection="column">
          <StatCount
            color={theme.color.turquoise70}
            count={exercisedCount}
            testId="exercised-routes-summary-count"
          />
          <Text font="metadata">
            {intl.formatMessage({ id: 'title_routesExercised' })}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  )
}

const overflowTextCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const styleColorBlue50 = { style: { color: theme.color.blue50 } }
type MetadataField = {
  label: string
  value: string
  isMostRecent?: boolean
}
const SessionMetadataSummary = ({ smdStats, sessionMetadataActionProps }) => {
  const intl = useIntl()
  const { smdCount, smdTotal = 1 } = smdStats
  const percentValue = Math.round((smdCount / (smdTotal || 1)) * 100)
  const [emptyFields, setEmptyFields] = React.useState<MetadataField[]>([])
  const [metadataFields, setMetadataFields] = React.useState<MetadataField[]>(
    []
  )
  const onSessionChange = React.useCallback(
    (session) => {
      setMetadataFields(
        session
          ? [
              {
                label: intl.formatMessage({
                  id: 'title_sessionMetadata_sessionColumnHeader'
                }),
                value: intl.formatDate(
                  session.createdDate * 1000,
                  dateFormats.DATETIME_LONG
                ),
                isMostRecent: session.isMostRecentSession
              },
              ...session.metadataSessions.map((it) => ({
                label: it.metadataField.displayLabel,
                value: it.value
              }))
            ]
          : emptyFields
      )
    },
    [intl, setMetadataFields, emptyFields]
  )

  React.useEffect(() => {
    const fields = sessionMetadataActionProps?.filterFields
      .filter((it) => it.id !== 'dissociated')
      .map((it) => ({
        label: it.name,
        value: ''
      }))

    if (fields.length && !emptyFields.length) {
      setEmptyFields(fields)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setEmptyFields, sessionMetadataActionProps?.filterFields])

  const fields = metadataFields.length ? metadataFields : emptyFields

  return (
    <Stack
      flexDirection="column"
      spacing="24px"
      testId="session-metadata-summary"
    >
      <Stack flex={1}>
        <Text font="titleH5" {...styleColorBlue50}>
          {intl.formatMessage({ id: 'title_sessionRoutes' })}
        </Text>
        <SessionMetadataActionBar
          onSessionChange={onSessionChange}
          {...sessionMetadataActionProps}
        />
      </Stack>
      <Stack alignItems="center" spacing="24px">
        <RoutesExercisedChart
          percentValue={percentValue}
          sliceColor={theme.color.blue50}
        />
        <Stack flexDirection="column">
          <StatCount
            color={theme.color.blue50}
            count={smdCount}
            testId="session-metadata-summary-count"
          />
          <Text font="metadata">
            {intl.formatMessage({ id: 'title_routesExercised' })}
          </Text>
        </Stack>
        <Stack
          spacing="24px"
          className={css`
            flex: 1;
            flex-wrap: wrap;
            align-self: flex-start;
          `}
        >
          {fields.map((it, index) => (
            <Stack
              key={index}
              flexDirection="column"
              className={css`
                min-width: 30%;
                max-width: 30%;
              `}
            >
              <Text
                as="div"
                font="titleH6"
                className={overflowTextCss}
                style={{ textTransform: 'capitalize' }}
              >
                {!it.isMostRecent ? (
                  it.label
                ) : (
                  <Stack alignItems="baseline" spacing="8px">
                    {it.label}
                    <InformationalPill
                      style={{ fontWeight: 'bold', height: 'fit-content' }}
                      text={intl.formatMessage({ id: 'title_recent' })}
                    />
                  </Stack>
                )}
              </Text>
              <Text as="div" font="body" className={overflowTextCss}>
                {it.value}
              </Text>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  )
}

const chartSize = 200
const RoutesExercisedChart = ({
  percentValue,
  sliceColor = theme.color.turquoise70
}) => {
  const intl = useIntl()

  return (
    <Chart
      testId="routes-exercised-chart"
      options={{
        series: [
          {
            type: 'pie',
            innerSize: '80%',
            fillColor: theme.color.neutral40,
            enableMouseTracking: false,
            data: [
              ['ROUTES_NOT_EXERCISED', 100 - (percentValue || 0)],
              ['ROUTES_EXERCISED', percentValue || 0]
            ]
          }
        ],
        tooltip: { enabled: false },
        title: {
          text: ReactDOMServer.renderToStaticMarkup(
            <Stack flexDirection="column" alignItems="center">
              <Text
                as="div"
                font="metricLg"
                style={{
                  color:
                    typeof percentValue !== 'undefined' && !isNaN(percentValue)
                      ? sliceColor
                      : theme.color.neutral50
                }}
              >
                {formatEmptyState(percentValue)} %
              </Text>
              <Text as="div" font="metadata">
                {intl.formatMessage({ id: 'title_routesExercised' })}
              </Text>
            </Stack>
          ),
          align: 'center',
          verticalAlign: 'middle',
          useHTML: true
        },
        chart: {
          height: chartSize,
          width: chartSize,
          margin: 0
        },
        plotOptions: {
          pie: {
            dataLabels: {
              enabled: false
            },
            borderWidth: 5,
            colors: [theme.color.neutral40, sliceColor],
            size: chartSize,
            animation: false
          }
        }
      }}
    />
  )
}
